/**
 * "Set up structures" — one wizard, one commit:
 *
 *   Step 1  Structures: name each proposal structure and (optionally) tie the timber-list
 *           version whose CSV fills it. A CSV with several Buildings can become one
 *           structure per Building (a structure *group* on Files).
 *   Step 2  Group the rest of the project: the other whole-project tracks (2D, 3D, HTML…)
 *           fork into structure groups — one per structure by default, merge as needed.
 *
 * When the proposal already lists structures and there is nothing left to tie, the
 * wizard opens straight on step 2 (split only).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, GitFork, Layers, Loader2, Plus, X } from 'lucide-react';
import type { ProjectFile, ProjectFileStream } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { headVersionForStream, versionsForStream } from '../../utils/projectFileStreamVersions';
import {
  detectTimberListGroups,
  isLiveStructureRow,
  seedStructureRowsFromTracks,
  suggestStructureNameFromTrack,
  timberListTracksForStructureSetup,
  timberListVersionOptionLabel,
  type StructureFromTimberListRow,
} from '../../utils/proposalStructuresFromTimberLists';
import {
  defaultStructureGroups,
  emptyStructureGroup,
  groupLabel,
  splitCandidateTracks,
  structureSlotsForSetup,
  trackOutcome,
  type ProjectStructureSetupPlan,
  type StructureGroupDraft,
  type StructureSlot,
  type TrackGroupSelection,
} from '../../utils/projectStructureSetup';
import { trackKindShort } from './projectUploadModalShared';
import StructureCheckDropdown from './StructureCheckDropdown';
import './ProjectFiles.css';
import './SplitTracksModal.css';
import './CreateStructuresFromTimberListsModal.css';

export interface StructureSetupWizardProps {
  /** Every track on the project (the wizard picks its own candidates). */
  streams: ProjectFileStream[];
  files: ProjectFile[];
  /** Structures already on the proposal. */
  structures: StructureRef[];
  versionCounts: Map<string, number>;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (plan: ProjectStructureSetupPlan) => void;
  /** Injectable CSV loader (group detection). */
  fetchText?: (url: string) => Promise<string>;
}

let keySeq = 0;
const nextKey = () => `tl-row-${++keySeq}`;
const NEW_VALUE = '__new__';
const NONE_VALUE = '';

const emptyRow = (): StructureFromTimberListRow => ({
  key: nextKey(),
  name: '',
  existingStructureId: null,
  timberListStreamId: null,
  timberListFileId: null,
});

/** Validation for step 1: duplicate names / structures, or a timber list used twice. */
export function structureRowsProblem(
  rows: ReadonlyArray<StructureFromTimberListRow>,
  structures: ReadonlyArray<StructureRef>,
): string | null {
  const live = rows.filter(isLiveStructureRow);
  if (live.length === 0) return 'Name at least one structure.';
  const names = new Set<string>();
  const existing = new Set<string>();
  const fileIds = new Set<string>();
  const takenNames = new Set(structures.map(s => s.name.trim().toLowerCase()));
  for (const r of live) {
    if (r.existingStructureId) {
      if (existing.has(r.existingStructureId)) return 'The same proposal structure is picked twice.';
      existing.add(r.existingStructureId);
    } else if (!(r.splitByGroup && (r.groupNames?.length ?? 0) > 1)) {
      const key = r.name.trim().toLowerCase();
      if (names.has(key)) return `Two rows are both named “${r.name.trim()}”.`;
      if (takenNames.has(key)) return `The proposal already has a structure named “${r.name.trim()}” — pick “Use existing” for it.`;
      names.add(key);
    }
    if (r.timberListStreamId && !r.timberListFileId) return 'Pick a version for each timber list track (or choose None).';
    if (r.timberListFileId) {
      if (fileIds.has(r.timberListFileId)) return 'The same timber-list version is selected twice.';
      fileIds.add(r.timberListFileId);
    }
  }
  return null;
}

const defaultFetchText = async (url: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.text();
};

type GroupDetection = { state: 'loading' } | { state: 'done'; groups: string[] } | { state: 'error' };

const StructureSetupWizard: React.FC<StructureSetupWizardProps> = ({
  streams,
  files,
  structures,
  versionCounts,
  busy = false,
  onCancel,
  onConfirm,
  fetchText = defaultFetchText,
}) => {
  const timberListTracks = useMemo(() => timberListTracksForStructureSetup(streams), [streams]);
  // Step 1 exists when there are timber lists to tie, or no structures at all yet (name them by hand).
  const hasStep1 = timberListTracks.length > 0 || structures.length === 0;

  const [rows, setRows] = useState<StructureFromTimberListRow[]>(() => {
    if (!hasStep1) return [];
    const seeded = seedStructureRowsFromTracks(timberListTracks, files);
    return seeded.length ? seeded : [emptyRow()];
  });
  const [step, setStep] = useState<1 | 2>(hasStep1 ? 1 : 2);

  // ── Step 1 helpers ──
  const versionsOnStream = useCallback(
    (streamId: string | null) => {
      if (!streamId) return [];
      const stream = timberListTracks.find(s => s.id === streamId);
      return stream ? versionsForStream(stream, files) : [];
    },
    [timberListTracks, files],
  );
  const usedFileIdsExcept = (rowKey: string) =>
    new Set(rows.filter(o => o.key !== rowKey && o.timberListFileId).map(o => o.timberListFileId!));
  const defaultFileIdForStream = (streamId: string | null, rowKey: string): string | null => {
    if (!streamId) return null;
    const used = usedFileIdsExcept(rowKey);
    const free = versionsOnStream(streamId).find(v => v.id && !used.has(v.id));
    if (free?.id) return free.id;
    const stream = timberListTracks.find(s => s.id === streamId);
    return headVersionForStream(stream, files)?.id ?? stream?.headFileId ?? null;
  };
  const nameFromVersionFile = (fileId: string | null, streamId: string | null): string => {
    if (!fileId || !streamId) return '';
    const v = versionsOnStream(streamId).find(f => f.id === fileId);
    return v?.name ? suggestStructureNameFromTrack(v.name) : '';
  };
  const updateRow = (key: string, fn: (r: StructureFromTimberListRow) => StructureFromTimberListRow) =>
    setRows(prev => prev.map(r => (r.key === key ? fn(r) : r)));

  // Group detection: read each chosen CSV once and remember its Building / Room groups.
  const [detections, setDetections] = useState<Record<string, GroupDetection>>({});
  const detectionsRef = useRef(detections);
  detectionsRef.current = detections;
  useEffect(() => {
    const wanted = Array.from(new Set(rows.map(r => r.timberListFileId).filter((id): id is string => !!id)));
    for (const fileId of wanted) {
      if (detectionsRef.current[fileId]) continue;
      const file = files.find(f => f.id === fileId);
      if (!file?.url) continue;
      setDetections(prev => ({ ...prev, [fileId]: { state: 'loading' } }));
      fetchText(file.url)
        .then(text => {
          const groups = detectTimberListGroups(text, { trueDimensions: file.trueDimensions === true });
          setDetections(prev => ({ ...prev, [fileId]: { state: 'done', groups } }));
          setRows(prev =>
            prev.map(r => (r.timberListFileId === fileId ? { ...r, groupNames: groups.length > 1 ? groups : undefined } : r)),
          );
        })
        .catch(() => setDetections(prev => ({ ...prev, [fileId]: { state: 'error' } })));
    }
  }, [rows, files, fetchText]);

  const live = useMemo(() => rows.filter(isLiveStructureRow), [rows]);
  const slots = useMemo(() => structureSlotsForSetup(rows, structures), [rows, structures]);
  const candidates = useMemo(() => splitCandidateTracks(streams, rows), [streams, rows]);
  const hasStep2 = candidates.length > 0 && slots.length >= 2;
  const canSkipStep1 = structures.length > 0 && hasStep2;
  const step1Problem = live.length === 0 ? (canSkipStep1 ? null : 'Name at least one structure.') : structureRowsProblem(rows, structures);

  // ── Step 2 state (groups over slots; per-track group selection) — reconciled when slots / candidates change ──
  const [groups, setGroups] = useState<StructureGroupDraft[]>([]);
  const [trackGroups, setTrackGroups] = useState<Record<string, TrackGroupSelection>>({});
  const slotSig = slots.map(s => s.id).join('|');
  const lastSlotSig = useRef<string | null>(null);
  useEffect(() => {
    if (lastSlotSig.current === slotSig) return;
    const first = lastSlotSig.current === null;
    lastSlotSig.current = slotSig;
    const known = new Set(slots.map(s => s.id));
    setGroups(prev => {
      if (first) return defaultStructureGroups(slots);
      const kept = prev.map(g => ({ ...g, slotIds: g.slotIds.filter(id => known.has(id)) }));
      const claimed = new Set(kept.flatMap(g => g.slotIds));
      const fresh = defaultStructureGroups(slots.filter(s => !claimed.has(s.id)));
      return [...kept.filter(g => g.slotIds.length > 0 || prev.length === 1), ...fresh];
    });
  }, [slotSig, slots]);
  const candSig = candidates.map(s => s.id).join('|');
  const lastCandSig = useRef<string | null>(null);
  useEffect(() => {
    if (lastCandSig.current === candSig) return;
    const first = lastCandSig.current === null;
    lastCandSig.current = candSig;
    const ids = candidates.map(s => s.id!).filter(Boolean);
    setTrackGroups(prev => {
      const next: Record<string, TrackGroupSelection> = {};
      for (const id of ids) next[id] = first ? 'all' : prev[id] ?? 'all';
      return next;
    });
  }, [candSig, candidates]);

  const updateGroup = (key: string, fn: (g: StructureGroupDraft) => StructureGroupDraft) =>
    setGroups(prev => prev.map(g => (g.key === key ? fn(g) : g)));
  const liveGroups = useMemo(() => groups.filter(g => g.slotIds.length > 0), [groups]);
  const liveGroupKeys = useMemo(() => new Set(liveGroups.map(g => g.key)), [liveGroups]);
  const claimedSlots = useMemo(() => new Set(groups.flatMap(g => g.slotIds)), [groups]);
  const unclaimed = slots.filter(s => !claimedSlots.has(s.id));
  /** Explicit selections only ever reference live groups (a removed group drops out). */
  const selectionFor = (streamId: string): TrackGroupSelection => {
    const sel = trackGroups[streamId];
    if (!sel || sel === 'all') return sel ?? 'all';
    return sel.filter(k => liveGroupKeys.has(k));
  };
  const outcomes = useMemo(() => {
    const m = new Map<string, ReturnType<typeof trackOutcome>>();
    for (const s of candidates) {
      if (!s.id) continue;
      const sel = trackGroups[s.id];
      m.set(s.id, trackOutcome(sel === 'all' || !sel ? sel : sel.filter(k => liveGroupKeys.has(k)), liveGroups));
    }
    return m;
  }, [candidates, trackGroups, liveGroups, liveGroupKeys]);
  const includedCount = Array.from(outcomes.values()).filter(o => o.kind !== 'whole').length;
  const forkedTrackCount = Array.from(outcomes.values()).filter(o => o.kind === 'fork').length;
  const taggedTrackCount = Array.from(outcomes.values()).filter(o => o.kind === 'tag').length;
  const newTrackCount = Array.from(outcomes.values()).reduce((n, o) => n + (o.kind === 'fork' ? o.groups.length : 0), 0);
  const anySelected = Object.values(trackGroups).some(sel => sel === 'all' || sel.length > 0);
  const step2Problem =
    liveGroups.length === 0 && anySelected
      ? 'Pick at least one structure for a group.'
      : live.length === 0 && includedCount === 0
        ? 'Nothing to do — tie a timber list on step 1 or place a track under a group.'
        : null;

  const setTrackSelection = (id: string, sel: TrackGroupSelection) => setTrackGroups(prev => ({ ...prev, [id]: sel }));
  const groupOptions = useMemo(
    () => liveGroups.map(g => ({ id: g.key, name: groupLabel(g, slots) })),
    [liveGroups, slots],
  );

  useEffect(() => {
    if (busy) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const newStructureCount = live.reduce((n, r) => n + (r.existingStructureId ? 0 : r.splitByGroup && (r.groupNames?.length ?? 0) > 1 ? r.groupNames!.length : 1), 0);
  const existingCount = live.filter(r => r.existingStructureId).length;
  const tiedCount = live.filter(r => r.timberListStreamId).length;
  const confirm = () => {
    const planTracks: Record<string, TrackGroupSelection> = {};
    if (step === 2) {
      for (const s of candidates) {
        if (!s.id) continue;
        const sel = selectionFor(s.id);
        if (sel === 'all' || sel.length > 0) planTracks[s.id] = sel;
      }
    }
    onConfirm({
      rows: live,
      groups: step === 2 && includedCount > 0 ? liveGroups : [],
      trackGroups: planTracks,
    });
  };

  const confirmLabel = (() => {
    const parts: string[] = [];
    if (newStructureCount) parts.push(`Create ${newStructureCount} structure${newStructureCount !== 1 ? 's' : ''}`);
    else if (existingCount) parts.push(`Fill ${existingCount} structure${existingCount !== 1 ? 's' : ''}`);
    if (step === 2 && includedCount > 0) {
      if (forkedTrackCount && !taggedTrackCount) {
        const allEvery = newTrackCount === forkedTrackCount * liveGroups.length;
        parts.push(
          allEvery
            ? `split ${forkedTrackCount} track${forkedTrackCount !== 1 ? 's' : ''} into ${liveGroups.length} group${liveGroups.length !== 1 ? 's' : ''}`
            : `split ${forkedTrackCount} track${forkedTrackCount !== 1 ? 's' : ''} into ${newTrackCount} new track${newTrackCount !== 1 ? 's' : ''}`,
        );
      } else if (taggedTrackCount && !forkedTrackCount) {
        parts.push(`tag ${taggedTrackCount} track${taggedTrackCount !== 1 ? 's' : ''} to ${taggedTrackCount === 1 ? 'its' : 'their'} group`);
      } else {
        parts.push(`place ${includedCount} track${includedCount !== 1 ? 's' : ''} under ${liveGroups.length} group${liveGroups.length !== 1 ? 's' : ''}`);
      }
    }
    if (!parts.length) return 'Done';
    const s = parts.join(' & ');
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  const trackLabel = (s: ProjectFileStream) => {
    const n = s.id ? versionCounts.get(s.id) ?? 0 : 0;
    return `${s.title} · ${n} version${n !== 1 ? 's' : ''}`;
  };

  // ── Render: step 1 ──
  const renderStep1 = () => (
    <>
      <p className="pf-modal-hint">
        Each structure gets its own entry on the proposal, exactly as a Cadwork import would create it. Tie a timber-list
        track to a structure and pick which version supplies the CSV — several structures can come from different
        versions on the same whole-project track (each extra version moves onto its own track under its structure).
        When one CSV covers several Buildings, you can create one structure per Building; that track then belongs to the
        whole group. Tying a timber list is optional.
        {structures.length ? ' Pick “Use existing” to fill a structure the proposal already lists.' : ''}
      </p>
      <section className="pf-split-section" aria-labelledby="pf-structures-rows-title">
        <h4 className="pf-split-section-title" id="pf-structures-rows-title">
          Structures
          <span className="pf-split-section-meta">
            {newStructureCount + existingCount} structure{newStructureCount + existingCount !== 1 ? 's' : ''} · {tiedCount} timber list
            {tiedCount !== 1 ? 's' : ''} tied
          </span>
        </h4>
        <div className="pf-split-targets">
          {rows.map((r, idx) => {
            const usedExisting = new Set(rows.filter(o => o.key !== r.key && o.existingStructureId).map(o => o.existingStructureId));
            const tied = r.timberListStreamId ? timberListTracks.find(s => s.id === r.timberListStreamId) : undefined;
            const trackVersions = versionsOnStream(r.timberListStreamId);
            const usedFileIds = usedFileIdsExcept(r.key);
            const detection = r.timberListFileId ? detections[r.timberListFileId] : undefined;
            const detectedGroups = detection?.state === 'done' && detection.groups.length > 1 ? detection.groups : null;
            const splitting = !r.existingStructureId && !!r.splitByGroup && !!detectedGroups;
            return (
              <div key={r.key} className="pf-split-target pf-structures-row">
                <span className="pf-split-target-n" aria-hidden>
                  {idx + 1}
                </span>
                <div className="pf-split-target-fields">
                  {structures.length ? (
                    <label className="pf-split-field pf-structures-field--mode">
                      <span className="pf-vr-mu">Structure</span>
                      <select
                        className="pf-tag-select pf-text-input-height"
                        value={r.existingStructureId ?? NEW_VALUE}
                        disabled={busy}
                        onChange={e =>
                          updateRow(r.key, cur => ({
                            ...cur,
                            existingStructureId: e.target.value === NEW_VALUE ? null : e.target.value,
                          }))
                        }
                      >
                        <option value={NEW_VALUE}>New structure…</option>
                        {structures.map(s => (
                          <option key={s.id} value={s.id} disabled={usedExisting.has(s.id)}>
                            Use existing: {s.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {r.existingStructureId || splitting ? null : (
                    <label className="pf-split-field pf-split-field--grow">
                      <span className="pf-vr-mu">Structure name *</span>
                      <input
                        type="text"
                        className="pf-text-input pf-text-input-height"
                        value={r.name}
                        placeholder={tied ? `e.g. ${tied.title}` : 'e.g. Barn'}
                        disabled={busy}
                        onChange={e => updateRow(r.key, cur => ({ ...cur, name: e.target.value }))}
                      />
                    </label>
                  )}
                  {splitting ? (
                    <div className="pf-split-field pf-split-field--grow">
                      <span className="pf-vr-mu">Structures (one per Building in the CSV)</span>
                      <div className="pf-text-input pf-text-input-height pf-structures-groupnames" title={detectedGroups!.join(', ')}>
                        {detectedGroups!.join(' + ')}
                      </div>
                    </div>
                  ) : null}
                  <label className="pf-split-field pf-split-field--grow">
                    <span className="pf-vr-mu">Timber list (optional)</span>
                    <select
                      className="pf-tag-select pf-text-input-height"
                      value={r.timberListStreamId ?? NONE_VALUE}
                      disabled={busy}
                      onChange={e => {
                        const streamId = e.target.value || null;
                        const fileId = defaultFileIdForStream(streamId, r.key);
                        const suggested = nameFromVersionFile(fileId, streamId);
                        updateRow(r.key, cur => ({
                          ...cur,
                          timberListStreamId: streamId,
                          timberListFileId: fileId,
                          splitByGroup: false,
                          groupNames: undefined,
                          name: !cur.name.trim() && suggested ? suggested : cur.name,
                        }));
                      }}
                    >
                      <option value={NONE_VALUE}>None — create empty</option>
                      {timberListTracks.map(s =>
                        s.id ? (
                          <option key={s.id} value={s.id}>
                            {trackKindShort(s.streamKind)} · {trackLabel(s)}
                          </option>
                        ) : null,
                      )}
                    </select>
                  </label>
                  {r.timberListStreamId && trackVersions.length > 0 ? (
                    <label className="pf-split-field pf-split-field--grow">
                      <span className="pf-vr-mu">Version</span>
                      <select
                        className="pf-tag-select pf-text-input-height"
                        value={r.timberListFileId ?? defaultFileIdForStream(r.timberListStreamId, r.key) ?? NONE_VALUE}
                        disabled={busy}
                        onChange={e => {
                          const fileId = e.target.value || null;
                          const suggested = nameFromVersionFile(fileId, r.timberListStreamId);
                          updateRow(r.key, cur => ({
                            ...cur,
                            timberListFileId: fileId,
                            splitByGroup: false,
                            groupNames: undefined,
                            name: !cur.name.trim() && suggested ? suggested : cur.name,
                          }));
                        }}
                      >
                        {trackVersions.map(v =>
                          v.id ? (
                            <option key={v.id} value={v.id} disabled={usedFileIds.has(v.id)}>
                              {timberListVersionOptionLabel(v)}
                            </option>
                          ) : null,
                        )}
                      </select>
                    </label>
                  ) : null}
                  {detectedGroups && !r.existingStructureId ? (
                    <label className="pf-structures-groups-toggle">
                      <input
                        type="checkbox"
                        checked={!!r.splitByGroup}
                        disabled={busy}
                        onChange={e =>
                          updateRow(r.key, cur => ({ ...cur, splitByGroup: e.target.checked, groupNames: detectedGroups }))
                        }
                      />
                      <span>
                        This CSV covers <strong>{detectedGroups.length}</strong> Buildings ({detectedGroups.join(', ')}) — create one
                        structure per Building and keep them together as a group
                      </span>
                    </label>
                  ) : detection?.state === 'loading' ? (
                    <span className="pf-structures-detect-note">
                      <Loader2 size={11} className="pf-spin" aria-hidden /> Reading the CSV…
                    </span>
                  ) : null}
                </div>
                {rows.length > 1 ? (
                  <button
                    type="button"
                    className="pf-icon-btn pf-split-target-remove"
                    aria-label="Remove this row"
                    disabled={busy}
                    onClick={() => setRows(prev => prev.filter(o => o.key !== r.key))}
                  >
                    <X size={13} />
                  </button>
                ) : null}
              </div>
            );
          })}
          <button type="button" className="pf-split-add" disabled={busy} onClick={() => setRows(prev => [...prev, emptyRow()])}>
            <Plus size={12} aria-hidden /> Add a structure
          </button>
        </div>
      </section>
      {live.length ? (
        <p className="pf-split-preview">
          Step 1 result: <strong>{newStructureCount}</strong> new structure{newStructureCount !== 1 ? 's' : ''}
          {existingCount ? (
            <>
              , <strong>{existingCount}</strong> existing filled
            </>
          ) : null}
          {tiedCount ? (
            <>
              ; <strong>{tiedCount}</strong> timber list{tiedCount !== 1 ? 's' : ''} tied
            </>
          ) : null}
          .{' '}
          {hasStep2
            ? `Next: decide how the other ${candidates.length} whole-project track${candidates.length !== 1 ? 's' : ''} continue under these structures.`
            : candidates.length
              ? 'The other whole-project tracks can be split once the proposal lists two or more structures.'
              : ''}
        </p>
      ) : null}
      {step1Problem && live.length > 0 ? <p className="pf-split-problem">{step1Problem}</p> : null}
    </>
  );

  // ── Render: step 2 ──
  const renderStep2 = () => (
    <>
      <p className="pf-modal-hint">
        {hasStep1 ? (
          <>
            The structures from step 1
            {structures.length ? ' and the ones already on the proposal' : ''} are listed below as groups — one group per
            structure to start.
          </>
        ) : (
          <>
            The proposal lists <strong>{structures.length}</strong> structure{structures.length !== 1 ? 's' : ''} (
            {structures.map(s => s.name).join(', ')}). Everything uploaded so far was whole-project work.
          </>
        )}{' '}
        Merge structures into one group when a single model or plan set serves them all (a CSV split by Building is
        already one group). <strong>Every</strong> checked track continues under each group, starting from the versions
        you already have. Nothing is deleted; the originals stay as read-only whole-project roots.
      </p>

      <section className="pf-split-section" aria-labelledby="pf-split-groups-title">
        <h4 className="pf-split-section-title" id="pf-split-groups-title">
          Structure groups
          <span className="pf-split-section-meta">
            {liveGroups.length} group{liveGroups.length !== 1 ? 's' : ''}
            {unclaimed.length ? ` · not covered: ${unclaimed.map(s => s.name).join(', ')}` : ' · every structure covered'}
          </span>
        </h4>
        <div className="pf-split-targets">
          {groups.map((g, idx) => {
            const others = new Set<string>();
            groups.forEach(o => {
              if (o.key !== g.key) o.slotIds.forEach(id => others.add(id));
            });
            const auto = groupLabel({ ...g, labelEdited: false }, slots);
            return (
              <div key={g.key} className="pf-split-target">
                <span className="pf-split-target-n" aria-hidden>
                  {idx + 1}
                </span>
                <div className="pf-split-target-fields">
                  <div className="pf-split-field">
                    <span className="pf-vr-mu">Structures</span>
                    <StructureCheckDropdown
                      hideClear
                      compact
                      keepOpenOnChange
                      aria-label={`Structures for group ${idx + 1}`}
                      placeholder="Pick structures…"
                      structures={slots as StructureSlot[]}
                      value={g.slotIds}
                      disabledIds={others}
                      disabled={busy}
                      onChange={ids => updateGroup(g.key, cur => ({ ...cur, slotIds: ids }))}
                    />
                  </div>
                  <label className="pf-split-field pf-split-field--grow">
                    <span className="pf-vr-mu">Group name (prefixes each new track)</span>
                    <input
                      type="text"
                      className="pf-text-input pf-text-input-height"
                      value={g.labelEdited ? g.label : g.slotIds.length ? auto : ''}
                      placeholder={g.slotIds.length ? auto : 'Pick structures first'}
                      disabled={busy}
                      onChange={e => updateGroup(g.key, cur => ({ ...cur, label: e.target.value, labelEdited: true }))}
                      onBlur={e => {
                        if (!e.target.value.trim()) updateGroup(g.key, cur => ({ ...cur, label: '', labelEdited: false }));
                      }}
                    />
                  </label>
                </div>
                {groups.length > 1 ? (
                  <button
                    type="button"
                    className="pf-icon-btn pf-split-target-remove"
                    aria-label="Remove this group"
                    disabled={busy}
                    onClick={() => setGroups(prev => prev.filter(o => o.key !== g.key))}
                  >
                    <X size={13} />
                  </button>
                ) : null}
              </div>
            );
          })}
          <button
            type="button"
            className="pf-split-add"
            disabled={busy || unclaimed.length === 0}
            title={unclaimed.length === 0 ? 'Every structure is already in a group — uncheck one above to split it off' : undefined}
            onClick={() => setGroups(prev => [...prev, emptyStructureGroup()])}
          >
            <Plus size={12} aria-hidden /> Add a group
          </button>
        </div>
      </section>

      <section className="pf-split-section" aria-labelledby="pf-split-tracks-title">
        <h4 className="pf-split-section-title" id="pf-split-tracks-title">
          Where each whole-project track continues
          <span className="pf-split-section-meta">
            {includedCount} of {candidates.length} · a track under one group is tagged to it; under several, it splits
          </span>
        </h4>
        <ul className="pf-split-track-list">
          {candidates.map(s => {
            if (!s.id) return null;
            const sid = s.id;
            const outcome = outcomes.get(sid) ?? { kind: 'whole' as const };
            const on = outcome.kind !== 'whole';
            const sel = selectionFor(sid);
            const value = sel === 'all' ? liveGroups.map(g => g.key) : sel;
            const versions = versionCounts.get(sid) ?? 0;
            const meta =
              outcome.kind === 'whole'
                ? 'stays Whole project'
                : outcome.kind === 'tag'
                  ? `tagged to ${groupLabel(outcome.group, slots)} — keeps its history, no new track`
                  : sel === 'all'
                    ? `${outcome.groups.length} new track${outcome.groups.length !== 1 ? 's' : ''} (every group), this one becomes the shared root`
                    : `${outcome.groups.length} new tracks (${outcome.groups.map(g => groupLabel(g, slots)).join(', ')}), this one becomes the shared root`;
            return (
              <li key={sid} className={`pf-split-track-item${on ? '' : ' pf-split-track-item--off'}`}>
                <div className="pf-split-track-check pf-split-track-check--groups">
                  <input
                    type="checkbox"
                    aria-label={on ? `Keep ${s.title} as Whole project` : `Place ${s.title} under every group`}
                    checked={on}
                    disabled={busy}
                    onChange={() => setTrackSelection(sid, on ? [] : 'all')}
                  />
                  <span className={`pf-stream-kind-badge pf-stream-kind-badge--col pf-stream-kind-${s.streamKind}`}>
                    {trackKindShort(s.streamKind)}
                  </span>
                  <span className="pf-split-track-item-text">
                    <strong>{s.title}</strong>
                    <span className="pf-split-track-meta">
                      {versions} version{versions !== 1 ? 's' : ''} · {meta}
                    </span>
                  </span>
                  <StructureCheckDropdown
                    compact
                    keepOpenOnChange
                    className="pf-split-track-groups"
                    aria-label={`Groups for ${s.title}`}
                    placeholder="Whole project"
                    structures={groupOptions}
                    value={value}
                    disabled={busy || liveGroups.length === 0}
                    onChange={ids => setTrackSelection(sid, ids.length === liveGroups.length ? 'all' : ids)}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {liveGroups.length > 0 && includedCount > 0 ? (
        <p className="pf-split-preview">
          Result: <strong>{liveGroups.length}</strong> structure group{liveGroups.length !== 1 ? 's' : ''} (
          {liveGroups.map(g => groupLabel(g, slots)).join('; ')}).{' '}
          {forkedTrackCount ? (
            <>
              <strong>{forkedTrackCount}</strong> track{forkedTrackCount !== 1 ? 's' : ''} split into{' '}
              <strong>{newTrackCount}</strong> new track{newTrackCount !== 1 ? 's' : ''}
              {taggedTrackCount ? '; ' : '.'}
            </>
          ) : null}
          {taggedTrackCount ? (
            <>
              <strong>{taggedTrackCount}</strong> track{taggedTrackCount !== 1 ? 's' : ''} tagged straight to{' '}
              {taggedTrackCount === 1 ? 'its' : 'their'} group (no copies).
            </>
          ) : null}
        </p>
      ) : null}
      {step2Problem ? <p className="pf-split-problem">{step2Problem}</p> : null}
    </>
  );

  const totalSteps = hasStep1 && hasStep2 ? 2 : 1;
  const stepLabel = step === 1 ? 'Structures' : 'Group the rest of the project';
  const canConfirm = !busy && (step === 1 ? !step1Problem : !step2Problem);

  const modal = (
    <div
      className="pf-modal-overlay"
      role="presentation"
      onMouseDown={e => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="pf-modal pf-modal--split pf-modal--structures"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pf-structures-title"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="pf-split-modal-head">
          <h3 className="pf-modal-title" id="pf-structures-title">
            {step === 1 ? <Layers size={16} aria-hidden /> : <GitFork size={16} aria-hidden />} Set up structures
            {totalSteps > 1 ? (
              <span className="pf-structures-step">
                Step {hasStep1 ? step : 1} of {totalSteps} · {stepLabel}
              </span>
            ) : (
              <span className="pf-structures-step">{stepLabel}</span>
            )}
          </h3>
          <button type="button" className="pf-split-modal-close" disabled={busy} aria-label="Close" onClick={onCancel}>
            <X size={16} aria-hidden />
          </button>
        </div>

        {step === 1 ? renderStep1() : renderStep2()}

        <div className="pf-modal-actions">
          {step === 2 && hasStep1 ? (
            <button type="button" className="pf-modal-cancel" disabled={busy} onClick={() => setStep(1)}>
              <ArrowLeft size={13} aria-hidden /> Back
            </button>
          ) : null}
          <button type="button" className="pf-modal-cancel" disabled={busy} onClick={onCancel}>
            Not now
          </button>
          {step === 1 && hasStep2 ? (
            <button type="button" className="pf-upload-btn pf-upload-btn--accent" disabled={!canConfirm} onClick={() => setStep(2)}>
              Next: group the tracks <ArrowRight size={14} aria-hidden />
            </button>
          ) : (
            <button type="button" className="pf-upload-btn pf-upload-btn--accent" disabled={!canConfirm} onClick={confirm}>
              {busy ? (
                <>
                  <Loader2 size={14} className="pf-spin" /> Working…
                </>
              ) : (
                <>
                  <Layers size={14} aria-hidden /> {confirmLabel}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default StructureSetupWizard;
